/**
 * Rechazo por estado de la versión en un flujo de IA (OT-0064).
 *
 * Los diálogos de IA reciben el error como código suelto (evento `error` del
 * stream o `ApiError` de la estimación o de la llamada). Si es un bloqueo por
 * estado, la versión se marca bloqueada en caché —el editor pasa a su vista
 * de bloqueo, que desmonta el diálogo— y el aviso viaja como toast, con la
 * misma copy que `AiErrorNotice` muestra para ese código.
 */

import { useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../api/client'
import { isVersionLockCode, markVersionLocked } from '../lib/versionLock'
import { pushToast } from '../stores/toastStore'

/** Devuelve `true` si el código era un bloqueo por estado y ya se aplicó. */
export function useAiVersionLock(
  artifactId: string,
  versionId: string | null | undefined,
): (code: string | undefined) => boolean {
  const { t } = useTranslation('ai')
  const queryClient = useQueryClient()
  return useCallback(
    (code: string | undefined) => {
      if (!isVersionLockCode(code)) return false
      markVersionLocked(queryClient, artifactId, versionId ?? null)
      pushToast({ kind: 'critical', message: t(`errors.${code}`), duration: 8000 })
      return true
    },
    [artifactId, versionId, queryClient, t],
  )
}

/** Aplica el bloqueo en cuanto aparece `code` (el error de una estimación o
 *  de un stream, que llegan como estado y no como callback). */
export function useAiVersionLockOn(
  artifactId: string,
  versionId: string | null | undefined,
  code: string | undefined,
): void {
  const report = useAiVersionLock(artifactId, versionId)
  useEffect(() => {
    report(code)
  }, [code, report])
}

/** Código del error si viene del API; los demás errores no tienen. */
export function apiErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined
}
